import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import api from "../../../services/api";
import MobileAppLayout from "../../../layouts/MobileAppLayout";
import { GameButton, GameCard, GameIcon } from "../../../components/game";

interface MetricDefinition {
  id: string;
  name: string;
  unit: string | null;
  description: string | null;
  measurement_interval_days: number;
  order_no: number;
  is_active: boolean;
}

// Interval options for measurement frequency
const INTERVAL_OPTIONS = [
  { value: 30, label: "1 luna" },
  { value: 60, label: "2 luni" },
  { value: 90, label: "3 luni" },
  { value: 180, label: "6 luni" },
  { value: 365, label: "1 an" },
];

// Default metric suggestions
const DEFAULT_METRICS = [
  { name: "Inaltime", unit: "cm", description: "Inaltimea sportivului" },
  { name: "Greutate", unit: "kg", description: "Greutatea sportivului" },
  { name: "Anvergura", unit: "cm", description: "Distanta dintre varfurile degetelor cu bratele intinse lateral" },
  { name: "Alonja", unit: "cm", description: "Inaltimea cu bratele intinse vertical" },
  { name: "Detenta verticala", unit: "cm", description: "Inaltimea sariturii verticale" },
  { name: "Sprint 20m", unit: "sec", description: "Timpul pe distanta de 20 metri" },
  { name: "Rezistenta cardio", unit: "min", description: "Timp alergare continua" },
  { name: "Flexibilitate", unit: "cm", description: "Test sit-and-reach" },
];

export default function AppCoachMetricsConfig() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [definitions, setDefinitions] = useState<MetricDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Add form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newMetric, setNewMetric] = useState({ name: "", unit: "", description: "", measurement_interval_days: 90 });

  // Edit form
  const [editingMetric, setEditingMetric] = useState<MetricDefinition | null>(null);

  useEffect(() => {
    loadDefinitions();
  }, []);

  const loadDefinitions = async () => {
    setLoading(true);
    try {
      const response = await api.get("/metrics/definitions");
      setDefinitions(response.data);
    } catch (err) {
      console.error("Failed to load definitions:", err);
    }
    setLoading(false);
  };

  const handleAddMetric = async () => {
    if (!newMetric.name.trim()) return;

    setSaving(true);
    try {
      await api.post("/metrics/definitions", {
        name: newMetric.name.trim(),
        unit: newMetric.unit.trim() || null,
        description: newMetric.description.trim() || null,
        measurement_interval_days: newMetric.measurement_interval_days,
      });
      setNewMetric({ name: "", unit: "", description: "", measurement_interval_days: 90 });
      setShowAddForm(false);
      await loadDefinitions();
    } catch (err: any) {
      console.error("Failed to add metric:", err);
      alert(err?.response?.data?.error || "Eroare la adaugare");
    }
    setSaving(false);
  };

  const handleAddDefaultMetric = async (metric: typeof DEFAULT_METRICS[0]) => {
    setSaving(true);
    try {
      await api.post("/metrics/definitions", {
        name: metric.name,
        unit: metric.unit,
        description: metric.description,
      });
      await loadDefinitions();
    } catch (err: any) {
      console.error("Failed to add metric:", err);
      if (err?.response?.data?.error?.includes("already exists")) {
        alert("Aceasta metrica exista deja");
      }
    }
    setSaving(false);
  };

  const handleUpdateMetric = async () => {
    if (!editingMetric || !editingMetric.name.trim()) return;

    setSaving(true);
    try {
      await api.put(`/metrics/definitions/${editingMetric.id}`, {
        name: editingMetric.name.trim(),
        unit: editingMetric.unit?.trim() || null,
        description: editingMetric.description?.trim() || null,
        measurement_interval_days: editingMetric.measurement_interval_days,
      });
      setEditingMetric(null);
      await loadDefinitions();
    } catch (err: any) {
      console.error("Failed to update metric:", err);
      alert(err?.response?.data?.error || "Eroare la actualizare");
    }
    setSaving(false);
  };

  const handleDeleteMetric = async (id: string) => {
    if (!window.confirm(t("coach.confirmDeleteMetric", "Ești sigur ca vrei sa stergi aceasta metrica? Toate valorile înregistrate vor fi sterse."))) {
      return;
    }

    try {
      await api.delete(`/metrics/definitions/${id}`);
      await loadDefinitions();
    } catch (err: any) {
      console.error("Failed to delete metric:", err);
      alert(err?.response?.data?.error || "Eroare la stergere");
    }
  };

  // Filter out already added metrics from suggestions
  const availableSuggestions = DEFAULT_METRICS.filter(
    dm => !definitions.some(d => d.name.toLowerCase() === dm.name.toLowerCase())
  );

  return (
    <MobileAppLayout>
      <div className="px-4 py-6 space-y-4 pb-24">
        {/* Header */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-slate-100 rounded-xl active:translate-y-[2px] transition-transform"
          >
            <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-extrabold text-slate-800">{t("coach.metricsConfig", "Configurare Metrici")}</h1>
            <p className="text-sm text-slate-500">{t("coach.metricsConfigDesc", "Defineste ce metrici vrei sa urmaresti")}</p>
          </div>
        </div>

        {/* Info Banner */}
        <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-4 shadow-[0_3px_0_#e0e7ff]">
          <div className="flex gap-3">
            <GameIcon name="bulb" className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-indigo-700">
              {t("coach.metricsConfigInfo", "Adauga metricile pe care vrei sa le urmaresti pentru sportivii tai. Poți folosi sugestiile de mai jos sau adauga metrici personalizate.")}
            </p>
          </div>
        </div>

        {/* Current Metrics */}
        <div>
          <h2 className="text-sm font-extrabold text-slate-700 mb-3">{t("coach.yourMetrics", "Metricile tale")}</h2>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-8 h-8 border-3 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : definitions.length === 0 ? (
            <div className="bg-slate-50 rounded-xl p-6 text-center">
              <svg className="w-12 h-12 text-slate-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <p className="text-slate-500">{t("coach.noMetricsYet", "Nu ai adăugat încă metrici")}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {definitions.map((def) => (
                <GameCard key={def.id} className="p-4">
                  {editingMetric?.id === def.id ? (
                    // Edit mode
                    <div className="space-y-3">
                      <input
                        type="text"
                        value={editingMetric.name}
                        onChange={(e) => setEditingMetric({ ...editingMetric, name: e.target.value })}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder={t("coach.metricName", "Nume metrica")}
                      />
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={editingMetric.unit || ""}
                          onChange={(e) => setEditingMetric({ ...editingMetric, unit: e.target.value })}
                          className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder={t("coach.unit", "Unitate (cm, kg, sec...)")}
                        />
                      </div>
                      <textarea
                        value={editingMetric.description || ""}
                        onChange={(e) => setEditingMetric({ ...editingMetric, description: e.target.value })}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        rows={2}
                        placeholder={t("coach.description", "Descriere (opțional)")}
                      />
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">{t("coach.measurementInterval", "Interval măsurare")}</label>
                        <select
                          value={editingMetric.measurement_interval_days}
                          onChange={(e) => setEditingMetric({ ...editingMetric, measurement_interval_days: Number(e.target.value) })}
                          className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          {INTERVAL_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex gap-2">
                        <GameButton variant="ghost" onClick={() => setEditingMetric(null)} className="px-4 py-2 text-sm">
                          {t("common.cancel", "Anulează")}
                        </GameButton>
                        <GameButton variant="primary" onClick={handleUpdateMetric} disabled={saving} className="px-4 py-2 text-sm">
                          {saving ? "..." : t("common.save", "Salvează")}
                        </GameButton>
                      </div>
                    </div>
                  ) : (
                    // View mode
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-extrabold text-slate-800">{def.name}</h3>
                          {def.unit && (
                            <span className="px-2 py-0.5 bg-slate-100 text-slate-500 text-xs font-bold rounded-full">{def.unit}</span>
                          )}
                          <span className="px-2 py-0.5 bg-indigo-50 text-indigo-600 text-xs font-bold rounded-full">
                            {INTERVAL_OPTIONS.find(o => o.value === def.measurement_interval_days)?.label || `${def.measurement_interval_days} zile`}
                          </span>
                        </div>
                        {def.description && (
                          <p className="text-xs text-slate-500 mt-1">{def.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setEditingMetric(def)}
                          className="p-2 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600 active:translate-y-[1px] transition-transform"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteMetric(def.id)}
                          className="p-2 hover:bg-red-50 rounded-lg text-slate-400 hover:text-red-600 active:translate-y-[1px] transition-transform"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                </GameCard>
              ))}
            </div>
          )}
        </div>

        {/* Add New Metric */}
        <div>
          {showAddForm ? (
            <GameCard className="p-4 border-indigo-200">
              <h3 className="font-extrabold text-slate-800 mb-3">{t("coach.addMetric", "Adauga metrica noua")}</h3>
              <div className="space-y-3">
                <input
                  type="text"
                  value={newMetric.name}
                  onChange={(e) => setNewMetric({ ...newMetric, name: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder={t("coach.metricName", "Nume metrica") + " *"}
                />
                <input
                  type="text"
                  value={newMetric.unit}
                  onChange={(e) => setNewMetric({ ...newMetric, unit: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder={t("coach.unit", "Unitate (cm, kg, sec...)")}
                />
                <textarea
                  value={newMetric.description}
                  onChange={(e) => setNewMetric({ ...newMetric, description: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  rows={2}
                  placeholder={t("coach.description", "Descriere (opțional)")}
                />
                <div>
                  <label className="block text-xs text-slate-500 mb-1">{t("coach.measurementInterval", "Interval măsurare")}</label>
                  <select
                    value={newMetric.measurement_interval_days}
                    onChange={(e) => setNewMetric({ ...newMetric, measurement_interval_days: Number(e.target.value) })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {INTERVAL_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-2">
                  <GameButton
                    variant="ghost"
                    onClick={() => {
                      setShowAddForm(false);
                      setNewMetric({ name: "", unit: "", description: "", measurement_interval_days: 90 });
                    }}
                    className="px-4 py-2 text-sm"
                  >
                    {t("common.cancel", "Anulează")}
                  </GameButton>
                  <GameButton
                    variant="primary"
                    onClick={handleAddMetric}
                    disabled={saving || !newMetric.name.trim()}
                    className="px-4 py-2 text-sm"
                  >
                    {saving ? "..." : t("common.add", "Adauga")}
                  </GameButton>
                </div>
              </div>
            </GameCard>
          ) : (
            <button
              onClick={() => setShowAddForm(true)}
              className="w-full py-3.5 border-2 border-dashed border-slate-300 rounded-2xl text-slate-500 font-bold hover:border-indigo-400 hover:text-indigo-600 active:translate-y-[2px] transition-all flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              {t("coach.addCustomMetric", "Adauga metrica personalizată")}
            </button>
          )}
        </div>

        {/* Suggestions */}
        {availableSuggestions.length > 0 && (
          <div>
            <h2 className="text-sm font-extrabold text-slate-700 mb-3">{t("coach.suggestions", "Sugestii")}</h2>
            <div className="grid grid-cols-2 gap-2">
              {availableSuggestions.map((metric, i) => (
                <button
                  key={i}
                  onClick={() => handleAddDefaultMetric(metric)}
                  disabled={saving}
                  className="p-3 bg-white rounded-2xl border border-slate-100 shadow-[0_3px_0_#eef2f8] text-left hover:border-indigo-300 hover:bg-indigo-50 active:translate-y-[2px] transition-all disabled:opacity-50"
                >
                  <p className="font-bold text-slate-800 text-sm">{metric.name}</p>
                  <p className="text-xs text-slate-400">{metric.unit}</p>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </MobileAppLayout>
  );
}
